import { useEffect, useRef, useState, useCallback } from 'react';

// Debounced autosave for property-doc fields. Replaces two near-identical 40-line
// useEffect blocks (buyerNote + showingDateTime) in LeadBuyerMatches.js.
//
// Pass:
//   value            — current local value
//   initial          — server-loaded initial value (sets the baseline that won't trigger save)
//   save(value)      — async save callback; returns when Firestore write resolves
//   delayMs          — debounce delay (default 1500ms)
//   enabled          — guard so we don't save before the page is ready
//
// Returns { saving, saved, error, flush }
//   flush — forced save (used by blur handlers).
//   error — set to a string when the last save failed; cleared when next save succeeds.
//
// On unmount with a pending dirty value, the save is fired-and-forgotten so the
// user doesn't lose the last <delayMs of typing on navigation. The setState
// calls inside doSave are gated by mountedRef so they no-op after unmount.
export function useDebouncedAutosave({ value, initial, save, delayMs = 1500, enabled = true }) {
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    const lastSavedRef = useRef(initial ?? '');
    const valueRef = useRef(value);
    const saveRef = useRef(save);
    const mountedRef = useRef(true);
    // Value currently mid-flight (between save() start and resolution). Lets
    // us dedupe a redundant unmount-flush against an already-in-flight write
    // for the same value, and lets us avoid stacking concurrent writes for
    // intermediate keystrokes.
    const inFlightRef = useRef(null);
    // Tail value that arrived while a save was in flight. Persisted after
    // the current save resolves so we don't drop the latest typing.
    const pendingTailRef = useRef(null);

    // Keep refs fresh so unmount-flush sees the latest value/save fn.
    useEffect(() => { valueRef.current = value; }, [value]);
    useEffect(() => { saveRef.current = save; }, [save]);

    // Track mount status so post-await setState calls after navigation no-op.
    useEffect(() => () => { mountedRef.current = false; }, []);

    // Keep the baseline in sync if the parent reloads the field (e.g. lead switch).
    useEffect(() => {
        lastSavedRef.current = initial ?? '';
        setSaved(false);
        setError(null);
    }, [initial]);

    const doSave = useCallback(async (next) => {
        if (next === lastSavedRef.current) return;
        // If the same value is already in flight, no-op. Catches the common
        // race: debounce timer fires, save starts, user navigates within
        // 1.5s, unmount-flush would otherwise issue a duplicate write.
        if (inFlightRef.current === next) return;
        // If a different save is in flight, stash this value as the tail —
        // the in-flight save's loop will pick it up. Prevents stacking
        // concurrent writes; the LAST value always wins.
        if (inFlightRef.current !== null) {
            pendingTailRef.current = next;
            return;
        }

        // Drain the queue serially. Each iteration persists one value; if
        // newer typing arrived during the save, the next iteration handles it.
        let current = next;
        let lastError = null;
        if (mountedRef.current) {
            setSaving(true);
            setSaved(false);
            setError(null);
        }
        try {
            while (current !== null && current !== lastSavedRef.current) {
                inFlightRef.current = current;
                try {
                    // eslint-disable-next-line no-await-in-loop -- serial drain
                    await saveRef.current(current);
                    lastSavedRef.current = current;
                    lastError = null;
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Autosave failed:', err);
                    lastError = err;
                    // Stop draining on error; the next user keystroke retries.
                    break;
                }
                current = pendingTailRef.current;
                pendingTailRef.current = null;
            }
            if (mountedRef.current) {
                if (lastError) setError(lastError?.message || 'Save failed');
                else setSaved(true);
            }
        } finally {
            inFlightRef.current = null;
            if (mountedRef.current) setSaving(false);
        }
    }, []);

    // Debounced background save.
    useEffect(() => {
        if (!enabled) return undefined;
        if (value === lastSavedRef.current) return undefined;
        const timer = setTimeout(() => {
            if (value !== lastSavedRef.current) doSave(value);
        }, delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs, enabled, doSave]);

    // Unmount-flush: if a dirty value is pending when the component tears down,
    // fire the save anyway. doSave is fire-and-forget; mountedRef gates setState
    // so the promise resolution can't write to an unmounted component.
    useEffect(() => () => {
        const pending = valueRef.current;
        if (enabled && pending !== lastSavedRef.current) {
            doSave(pending);
        }
    }, [enabled, doSave]);

    const flush = useCallback(() => doSave(valueRef.current), [doSave]);

    return { saving, saved, error, flush };
}
